import useCurrency from "~/hooks/currency";
import { useTaxExclusiveMode } from "~/hooks/tax";
import type { Price } from "~/types";

interface Props {
  price: Price;
  currency?: string;
  /**
   * The display mode for the component
   * - auto - Automatically decides between "tax_inclusive" and "tax_exclusive" based on useTaxExclusiveMode() hook
   * - tax_inclusive - Display the price.amount (which is tax inclusive)
   * - tax_exclusive - Display the tax exclusive amount (price.amount - price.taxIncluded)
   * - tax - Display the price.taxIncluded
   * - tax_not_applicable - Display the price.amount and bypass the check for including taxIncluded where required
   * - todo - Temporary mode to use while USD tax updates are being implemented (TODO: remove once USD tax completed)
   */
  mode?:
    | "auto"
    | "tax_inclusive"
    | "tax_exclusive"
    | "tax"
    | "tax_not_applicable"
    | "todo"; // TODO: remove once USD tax completed (remember to remove from JSDoc above)

  /**
   * If true omit decimal places when .00
   *
   * @default false
   */
  short?: boolean;
}

export function Currency(props: Props): React.ReactNode {
  const { price, short = false } = props;
  props.mode;
  const hookCurrency = useCurrency();
  const currency = props.currency || hookCurrency;

  const showTaxExclusive = useTaxExclusiveMode(currency);

  let mode = props.mode || "auto";
  if (mode === "auto") {
    mode = showTaxExclusive ? "tax_exclusive" : "tax_inclusive";
  }

  if (
    (price.taxIncluded === null || price.taxIncluded === undefined) &&
    (showTaxExclusive || mode === "tax") &&
    mode !== "tax_not_applicable" &&
    mode !== "todo"
  ) {
    throw new Error("Tax not available");
  }

  // price.taxIncluded type is still "number | null | undefined" here therefore using nullish coalescing operator below

  switch (mode) {
    case "tax_exclusive":
      return (
        <span>
          {formatCurrency(
            price.amount - (price.taxIncluded ?? 0),
            currency,
            short,
          )}
        </span>
      );
    case "tax":
      return (
        <span>{formatCurrency(price.taxIncluded ?? 0, currency, short)}</span>
      );
    case "tax_not_applicable":
    case "todo":
    case "tax_inclusive":
    default:
      return <span>{formatCurrency(price.amount, currency, short)}</span>;
  }
}

// If we want to handle localization, this function should probably take
// locale too, so (100, AUD, en-GB) -> AU$1.00 or similar
export function formatCurrency(
  value: number,
  currency: string,
  short: boolean,
): string {
  const sign = value < 0 ? "-" : "";

  // NOTE: all our currencies are currently 2 decimal places, if this
  // changes, just need to move this into the switch statement
  let str = (Math.abs(value) / 100).toFixed(2);
  if (short && str.endsWith(".00")) {
    str = str.slice(0, -3);
  }

  switch (currency) {
    case "AUD":
    case "NZD":
    case "SGD":
    case "USD":
      return `${sign}$${str}`;
    case "GBP":
      return `${sign}£${str}`;
  }
  return sign + str;
}
